import { cx } from '@emotion/css';
import { AddRounded, ArrowDownwardRounded, ArrowForwardOutlined, Check } from '@mui/icons-material';
import { Button, TYPOGRAPHY, useMediaQuery } from '@sortlist-frontend/design-system/legacy';
import { ImageSourceHelper } from '@sortlist-frontend/media';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { Fragment } from 'react';

import { ClickableTag } from '_components/common/ClickableTag';
import { QuoteCard } from '_components/common/QuoteCard';
import { usePublicAppContext } from '_core/context/public-app-context';
import { onImageError } from '_core/media/get-media.utils';
import { agencyQuotes, getFakeAgencies } from '_data/index';
import { providersConfig } from '_features/Providers/config';

import { BenefitsCards, Header } from './components';
import { getFreeBenefits, getSortlistPlusBenefits } from './data';
import * as S from './styles';
import { getPricingModules, PricingModule } from './utils';

type Props = {
  lang: string;
  origin: string;
};

export default function Providers(props: Props) {
  const { t } = useTranslation(providersConfig.i18nNamespaces);
  const { getUrl } = usePublicAppContext();
  const { media_legacy } = useMediaQuery();
  const providersPricingUrl = getUrl('providers.pricing');
  const agencyClaimLandingUrl = getUrl('agency.claim.landing');

  const modules = getPricingModules(t, media_legacy, getUrl);
  const agency = getFakeAgencies(t).find((agency) => agency.id === 'yourCompany') || getFakeAgencies(t)[0];

  const freeBenefits = getFreeBenefits(t);
  const SortlistPlusBenefits = getSortlistPlusBenefits(t);

  return (
    <Fragment>
      <Header
        className="bg-secondary-900"
        title={t('providers:header.title')}
        subtitle={t('providers:header.subtitle')}
        ctaLabel={t('providers:header.ctaLabel_V2')}
      />
      <main>
        <section data-testid="providers-free-section" className="bg-secondary-50 pb-16">
          <div className="py-16 sm:py-0 layout-column layout-align-center-center container-lg relative">
            <S.StyledFreeIllustration agency={agency} className="mb-16" />

            <div className="relative text-center layout-column layout-align-center-center width-100">
              <S.DashedLine className="rounded-full bg-primary-200 text-primary-500 p-2 layout-column layout-align-center-center mb-8">
                <ArrowDownwardRounded style={{ fontSize: TYPOGRAPHY.H4 }} />
              </S.DashedLine>
              <h2 className="h1 bold text-secondary-900 mb-4 lh-1">
                {
                  <Trans i18nKey={'providers:freeSection.title'}>
                    Start up with a <span className="text-primary-500">free profile</span>
                  </Trans>
                }
              </h2>
              <p className="h4 text-secondary-500 mb-8">{t('providers:freeSection.subtitle')}</p>

              <BenefitsCards className="my-8" isFree={true} benefits={freeBenefits} />
              <Button
                label={
                  <Trans i18nKey={'providers:freeSection.ctaLabel_V2'}>
                    Create my <span className="bold">free</span> profile
                  </Trans>
                }
                size="lg"
                buttonVariant="raised"
                buttonStyle="primary"
                onClick={() => (window.location.href = agencyClaimLandingUrl)}
                iconRight={<ArrowForwardOutlined />}
                id="open-claim-btn"
              />
            </div>
          </div>
        </section>
        <section data-testid="providers-sortlist-plus-section" className="bg-neutral-50 pt-24">
          <div className="container-md text-center relative">
            <S.SortlistPlusLogoWrapper className="monogram-sortlist-plus">
              <img
                src={ImageSourceHelper.fromPath('/_img/logos/sortlist/sortlist-plus-primary-negative.svg')}
                loading={'eager'}
                alt="Sortlist Plus logo"
                onError={onImageError}
                className="mb-4"
              />
            </S.SortlistPlusLogoWrapper>
            <S.ModuleTitle className="bold">
              <Trans i18nKey={'providers:SortlistPlusSection.title'}>
                <span className="serif">Generate more business by becoming Sortlist</span>+
              </Trans>
            </S.ModuleTitle>
            <p className="mt-4 h4 text-secondary-500 lh-2">
              <Trans i18nKey={'providers:SortlistPlusSection.subtitle_v4'}>
                Get advanced features out of the box <span className="bold text-primary-900">starting at 129€</span>
                /month
              </Trans>
            </p>
          </div>
          <div className="container-lg">
            <BenefitsCards className="my-8" benefits={SortlistPlusBenefits} />
          </div>
        </section>

        <section className="container-lg max-sm:px-0 relative">
          <S.AddWrapper className="layout-row layout-align-center-center">
            <div className="layout-column layout-align-center-center rounded-full text-neutral-100 bg-secondary-900 p-4">
              <AddRounded />
            </div>
          </S.AddWrapper>
          <S.VisibilityOpportunityWrapper className="mb-14 rounded-lg py-24">
            <header className="text-center max-sm:px-4">
              <p className="h4">{t('providers:modules.subTitle')}</p>
              <p className="text-primary-900 bold h2 mt-1">{t('providers:modules.title')}</p>
            </header>
            <div className="mt-8 lg:mt-16">
              {modules.map((module: PricingModule) => {
                const { id, inverted, palette, name, icon, title, description, illustration, items, ctaLink } = module;

                return (
                  <section
                    data-testid="providers-modules-sections"
                    key={id}
                    id={module.id}
                    className={cx({ 'bg-secondary-50': !inverted }, 'max-sm:py-8 overflow-hidden')}>
                    <div className="layout-column layout-gt-sm-row layout-align-start-center flex-1">
                      <div
                        className={cx(
                          inverted ? 'flex-order-1' : 'flex-order-gt-sm-0',
                          'flex-gt-sm-50 flex-100 layout-column layout-align-center-start text-left p-8 ',
                        )}>
                        <ClickableTag palette={palette} label={name} icon={icon} />
                        <S.TitleWithDote className="h1 bold mb-4">{title}</S.TitleWithDote>
                        <p className="h6 lh-3 mb-4 text-secondary-700">{description}</p>
                        {items && (
                          <ul className="mb-8 layout-column layout-align-start-start list-reset flex-1">
                            {items.map((item: string, index: number) => (
                              <li
                                key={'Advantage-' + id + '-item-' + index}
                                className={cx('py-1 layout-row layout-align-start-center')}>
                                <Check style={{ fontSize: TYPOGRAPHY.P }} className="text-success-500 mr-2 my-1" />
                                <span className="text-secondary-500 lh-2 text-left">{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <Button
                          className="underline"
                          label={t('providers:learnMore')}
                          buttonVariant="default"
                          buttonStyle="secondary"
                          size="md"
                          onClick={() => (window.location.href = ctaLink)}
                        />
                      </div>
                      <div
                        className={cx(
                          inverted
                            ? 'flex-order-gt-xs-0 layout-align-gt-xs-center-end'
                            : 'flex-order-gt-xs-1 layout-align-gt-xs-center-start',
                          'flex-order-xs-0 flex-gt-sm-50 flex-100 layout-column p-8 layout-align-center-center',
                        )}>
                        {illustration}
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          </S.VisibilityOpportunityWrapper>
        </section>

        <section className="testimonials bg-primary-900 py-16">
          <div
            data-testid="providers-testimonials"
            className="text-center bg-content-700 layout-column layout-align-center-center">
            <div className="container-lg">
              <QuoteCard quote={agencyQuotes(t)[9]} />
              <p className="h1 text-neutral-100 mt-14 mb-2 bold">{t('providers:cta.title')}</p>
              <p className="h6 text-secondary-300 mb-6">{t('providers:cta.subTitle_V2')}</p>
              <Button
                label={t('providers:cta.ctaLabel')}
                buttonVariant="raised"
                buttonStyle="secondary"
                size="md"
                onClick={() => (window.location.href = providersPricingUrl)}
                iconRight={<ArrowForwardOutlined />}
                id="open-pricing-btn"
              />
            </div>
          </div>
        </section>
      </main>
    </Fragment>
  );
}
